package org.zest.insan_360_website.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.zest.insan_360_website.components.widgets.DotLoading
import org.zest.insan_360_website.components.widgets.LanguageToggle
import org.zest.insan_360_website.components.widgets.QuestionIndexContainerStyle
import org.zest.insan_360_website.components.widgets.QuestionsIndexLabel
import org.zest.insan_360_website.components.widgets.QuestionsIndexSelect
import org.zest.insan_360_website.components.widgets.SessionExpiredModal
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.AssessmentPageState
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.CreditState
import org.zest.insan_360_website.data.page_states.CreditState.activeCreditSession
import org.zest.insan_360_website.data.page_states.CreditState.creditState
import org.zest.insan_360_website.data.page_states.CreditState.isSessionExpired
import org.zest.insan_360_website.data.page_states.CreditState.needUpdate
import org.zest.insan_360_website.model.CreditStatus
import org.zest.insan_360_website.utils.SessionTimerBar
import org.zest.insan_360_website.utils.SessionTimerUtils
import org.zest.insan_360_website.utils.SessionTimerUtils.rememberSessionTimerState

val AssessmentPageContentStyle = CssStyle {
    base { Modifier.fillMaxSize().padding(leftRight = 2.cssRem, topBottom = 4.cssRem) }
}

val assessmentWordLibrary = LanguageSettings.AssessmentPage

@OptIn(DelicateApi::class, ExperimentalComposeWebApi::class)
@Composable
@Layout
fun AssessmentPageLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val scope = rememberCoroutineScope()
    val answers = AssessmentPageState.answers
    val questionIndex = AssessmentPageState.index
    var isOpened by remember { mutableStateOf(false) }
    val breakpoint = rememberBreakpoint()
    val isIndexHidden = breakpoint < Breakpoint.XXL
    val timerState = rememberSessionTimerState(
        activeCreditSession,
        10 * 60 * 1000.0,
        2 * 60 * 1000.0
    ) { // NOTICE: change to fit backend time requirement
        isSessionExpired = true
    }

    LaunchedEffect(Unit) {
        CreditState.fetchCurrentCreditStatus()
        if (creditState != CreditStatus.IN_PROGRESS || !AuthState.isLoggedIn) {
            ctx.router.navigateTo("/user", UpdateHistoryMode.REPLACE)
        }
        CreditState.fetchActiveCreditSession()
        isSessionExpired = SessionTimerUtils.isSessionExpired(activeCreditSession)
    }

    DisposableEffect(Unit) {
        kotlinx.browser.window.history.pushState(js("{ isModalState: true }"), "", kotlinx.browser.window.location.href)
        val listener: (org.w3c.dom.events.Event) -> Unit = { event ->
            val isModalState = event.asDynamic().state?.isModalState == true
            if (!isModalState) {
                kotlinx.browser.window.history.forward()
            }
        }
        kotlinx.browser.window.addEventListener("popstate", listener)
        onDispose {
            kotlinx.browser.window.removeEventListener("popstate", listener)
        }
    }

    LaunchedEffect(needUpdate) {
        if (needUpdate) {
            CreditState.fetchActiveCreditSession()
            needUpdate = false
        }
    }

    LaunchedEffect(breakpoint) {
        if (breakpoint >= Breakpoint.XXL) {
            isOpened = false
        }
    }

    if (CreditState.isLoading || CreditState.isLoadingSession) {
        Box(
            Modifier
                .width(100.dvw)
                .height(100.dvh)
                .scale(2)
                .backgroundColor(userPageBGColor),
            contentAlignment = Alignment.Center
        ) {
            DotLoading()
        }
        return
    }

    SessionTimerBar(
        timerState,
        Modifier
            .position(Position.Absolute)
            .top(1.cssRem)
            .left(1.cssRem)
    )

    LanguageToggle(rightEm = 1.5, topEm = 1.5)

    if (isSessionExpired) {
        SessionExpiredModal(ctx, scope)
    }

    Box(
        modifier = Modifier
            .backgroundColor(userPageBGColor)
            .fillMaxWidth()
            .minHeight(100.vh)
            .userSelect(UserSelect.None),
        contentAlignment = Alignment.Center
    ) {
        Box(
            AssessmentPageContentStyle.toModifier(),
            contentAlignment = Alignment.Center
        ) { content() }
        Box(
            Modifier
                .fillMaxSize()
                .backgroundColor(Colors.Black)
                .opacity(0.4)
                .thenIf(!isOpened) {
                    Modifier.display(DisplayStyle.None)
                }
                .onClick { isOpened = false }
                .userSelect(UserSelect.None),
        )
        Row(
            modifier = Modifier
                .then(QuestionIndexContainerStyle.toModifier())
                .thenIf(isIndexHidden) {
                    Modifier.thenIf(isOpened) {
                        Modifier.styleModifier {
                            property("transform", "translateX(calc(50vw - 50%))")
                        }
                    }
                }
        ) {
            QuestionsIndexSelect(answers, questionIndex)
            QuestionsIndexLabel { if (isIndexHidden) isOpened = !isOpened }
        }
    }
}

